import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { getAudioContext, playSilentKick } from '@/audio/audioProcessor.ts';
import styles from './FullscreenTransport.module.css';

function formatTimecode(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toFixed(1).padStart(4, '0')}`;
}

const IconPlay = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.5 4.12v15.76a1 1 0 001.52.86l12.36-7.88a1 1 0 000-1.72L8.02 3.26a1 1 0 00-1.52.86z" />
  </svg>
);

const IconPause = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <rect x="5" y="4" width="5" height="16" rx="1" />
    <rect x="14" y="4" width="5" height="16" rx="1" />
  </svg>
);

const AUTO_HIDE_MS = 3000;

export function FullscreenTransport() {
  const { t } = useTranslation('transport');
  const currentTime = usePlaybackStore(s => s.currentTime);
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const togglePlayback = usePlaybackStore(s => s.togglePlayback);
  const shuttleSpeed = usePlaybackStore(s => s.shuttleSpeed);
  const getViewOffset = useProjectStore(s => s.getViewOffset);
  const getViewDuration = useProjectStore(s => s.getViewDuration);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes

  const viewOffset = getViewOffset();
  const viewDuration = getViewDuration();
  const displayTime = currentTime - viewOffset;

  const [visible, setVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetHideTimer = useCallback(() => {
    setVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
  }, []);

  useEffect(() => {
    resetHideTimer();
    const onMove = () => resetHideTimer();
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [resetHideTimer]);

  const toggleFullscreen = useUiStore(s => s.toggleFullscreen);
  const exitFullscreen = () => toggleFullscreen();

  return (
    <div className={`${styles.overlay} ${visible ? styles.visible : styles.hidden}`}>
      <div className={styles.bar}>
        <button className={styles.btn} onClick={() => {
          if (!isPlaying) { try { const ctx = getAudioContext(); ctx.resume().catch(() => {}); playSilentKick(ctx); } catch { /* ok */ } }
          togglePlayback();
        }}>
          {isPlaying ? <IconPause /> : <IconPlay />}
        </button>

        <span className={styles.timecode}>
          {formatTimecode(displayTime)} / {formatTimecode(viewDuration)}
        </span>

        {shuttleSpeed !== 1 && isPlaying && (
          <span className={styles.shuttle}>
            {Math.abs(shuttleSpeed)}x {shuttleSpeed > 0 ? '\u25B6' : '\u25C0'}
          </span>
        )}

        <div className={styles.spacer} />

        <button className={styles.btn} onClick={exitFullscreen} title={t('fullscreenExitTooltip')}>
          {t('fullscreenExitButton')}
        </button>
      </div>
    </div>
  );
}
